export const PLAN_TIERS = Object.freeze({
  UNKNOWN: "unknown",
  STARTER: "starter",
  BASIC: "basic",
  PROFESSIONAL: "professional",
});

export const FEATURE_GATE_ACTIONS = Object.freeze({
  UPGRADE: "upgrade",
  COMPLETE_SETUP: "complete_setup",
  CONTACT_ADMIN: "contact_admin",
  RETRY: "retry",
});

const PROFESSIONAL_PLAN_CODES = new Set(["pro", "professional", "team", "business", "enterprise"]);
const STARTER_PLAN_CODES = new Set(["starter", "free"]);
const TEAM_MEMBERSHIP_ROLE_VALUES = new Set(["team_owner", "team_staff"]);
const TEAM_PLAN_CODES = new Set(["team", "business", "enterprise"]);

const normalizeToken = (value) => String(value ?? "").trim().toLowerCase();

export function parsePlanTier(planValue, options = {}) {
  const { treatEmptyAsUnknown = true } = options;
  const normalized = normalizeToken(planValue);

  if (!normalized) {
    return treatEmptyAsUnknown ? PLAN_TIERS.UNKNOWN : PLAN_TIERS.STARTER;
  }

  if (normalized === PLAN_TIERS.UNKNOWN) {
    return PLAN_TIERS.UNKNOWN;
  }

  if (TEAM_MEMBERSHIP_ROLE_VALUES.has(normalized)) {
    return PLAN_TIERS.STARTER;
  }

  if (PROFESSIONAL_PLAN_CODES.has(normalized)) {
    return PLAN_TIERS.PROFESSIONAL;
  }

  if (normalized === "basic") return PLAN_TIERS.BASIC;

  if (STARTER_PLAN_CODES.has(normalized)) {
    return PLAN_TIERS.STARTER;
  }

  return PLAN_TIERS.STARTER;
}

export function isProfessionalPlan(planTierValue) {
  return [PLAN_TIERS.BASIC, PLAN_TIERS.PROFESSIONAL].includes(parsePlanTier(planTierValue));
}

export function planTierToPlanCode(planTierValue) {
  const tier = parsePlanTier(planTierValue, { treatEmptyAsUnknown: true });
  return tier === PLAN_TIERS.PROFESSIONAL ? "pro" : tier === PLAN_TIERS.BASIC ? "basic" : "starter";
}

export function planTierBadgeLabel(planTierValue) {
  const tier = parsePlanTier(planTierValue, { treatEmptyAsUnknown: true });
  if (tier === PLAN_TIERS.PROFESSIONAL) return "Professional Plus plan";
  if (tier === PLAN_TIERS.BASIC) return "Professional plan";
  if (tier === PLAN_TIERS.STARTER) return "Starter plan";
  return "Plan unavailable";
}

export function isTeamPlanCode(planValue) {
  const normalized = normalizeToken(planValue);
  if (!normalized || TEAM_MEMBERSHIP_ROLE_VALUES.has(normalized)) return false;
  return TEAM_PLAN_CODES.has(normalized);
}

const normalizeVerificationStatus = (status) => normalizeToken(status) || PLAN_TIERS.UNKNOWN;

const proPlanGate = (message) => ({
  title: "Professional plan required",
  message,
  actionLabel: "Upgrade to Professional",
  action: FEATURE_GATE_ACTIONS.UPGRADE,
});

const unknownPlanGate = () => ({
  title: "Unable to verify your plan",
  message: "We couldn't confirm your subscription right now. Refresh and try again.",
  actionLabel: "Retry",
  action: FEATURE_GATE_ACTIONS.RETRY,
});

export function createFeatureAccessSnapshot({
  planTier = PLAN_TIERS.UNKNOWN,
  isAdminUser = false,
  businessVerificationStatus = null,
  stripeStatus = null,
  stripeConfigured = false,
  phonePaymentsEnabled = false,
  hasInvoiceBankDetails = null,
} = {}) {
  return {
    planTier: parsePlanTier(planTier, { treatEmptyAsUnknown: true }),
    isAdminUser: Boolean(isAdminUser),
    businessVerificationStatus: businessVerificationStatus ?? null,
    stripeStatus: stripeStatus ?? null,
    stripeConfigured: Boolean(stripeConfigured),
    phonePaymentsEnabled: Boolean(phonePaymentsEnabled),
    hasInvoiceBankDetails: hasInvoiceBankDetails == null ? null : Boolean(hasInvoiceBankDetails),
  };
}

export function paymentsGate(snapshotInput) {
  const snapshot = createFeatureAccessSnapshot(snapshotInput);
  if ([PLAN_TIERS.BASIC, PLAN_TIERS.PROFESSIONAL].includes(snapshot.planTier)) return null;
  if (snapshot.planTier === PLAN_TIERS.STARTER) {
    return proPlanGate(
      "Payments, Tap to Pay, and Stripe payouts are available on the Professional plan.",
    );
  }
  return unknownPlanGate();
}

export function bookkeepingGate(snapshotInput) {
  const snapshot = createFeatureAccessSnapshot(snapshotInput);
  if ([PLAN_TIERS.BASIC, PLAN_TIERS.PROFESSIONAL].includes(snapshot.planTier)) return null;
  if (snapshot.planTier === PLAN_TIERS.STARTER) {
    return proPlanGate(
      "Bookkeeping, bank sync, mileage workflows, and reports are available on the Professional plan.",
    );
  }
  return unknownPlanGate();
}

export function bankingSyncGate(snapshotInput) {
  const snapshot = createFeatureAccessSnapshot(snapshotInput);
  if ([PLAN_TIERS.BASIC, PLAN_TIERS.PROFESSIONAL].includes(snapshot.planTier)) return null;
  if (snapshot.planTier === PLAN_TIERS.STARTER) {
    return proPlanGate("Banking sync is available on the Professional plan.");
  }
  return unknownPlanGate();
}

export function takePaymentGate(snapshotInput) {
  const snapshot = createFeatureAccessSnapshot(snapshotInput);
  const planGate = paymentsGate(snapshot);
  if (planGate) return planGate;

  if (!snapshot.isAdminUser) {
    return {
      title: "Admin access required",
      message: "Only an admin can enable and run Tap to Pay on this account.",
      actionLabel: "Contact admin",
      action: FEATURE_GATE_ACTIONS.CONTACT_ADMIN,
    };
  }

  const verificationStatus = normalizeVerificationStatus(snapshot.businessVerificationStatus);
  if (verificationStatus !== "approved") {
    if (verificationStatus === "pending") {
      return {
        title: "Business verification in review",
        message: "Your business verification is still pending. Payments unlock after approval.",
        actionLabel: "Open verification",
        action: FEATURE_GATE_ACTIONS.COMPLETE_SETUP,
      };
    }
    if (verificationStatus === "declined") {
      return {
        title: "Business verification needed",
        message: "Verification was declined. Update your details and resubmit to continue.",
        actionLabel: "Open verification",
        action: FEATURE_GATE_ACTIONS.COMPLETE_SETUP,
      };
    }
    return {
      title: "Complete verification first",
      message: "Finish business verification to unlock payments and Tap to Pay.",
      actionLabel: "Open verification",
      action: FEATURE_GATE_ACTIONS.COMPLETE_SETUP,
    };
  }

  const stripeStatus = normalizeToken(snapshot.stripeStatus);
  if (!snapshot.stripeConfigured || stripeStatus !== "enabled") {
    return {
      title: "Complete Stripe onboarding",
      message: "Stripe must be fully enabled before you can take payments.",
      actionLabel: "Complete setup",
      action: FEATURE_GATE_ACTIONS.COMPLETE_SETUP,
    };
  }

  if (!snapshot.phonePaymentsEnabled) {
    return {
      title: "Tap to Pay not enabled",
      message: "Enable Tap to Pay before starting checkout.",
      actionLabel: "Complete setup",
      action: FEATURE_GATE_ACTIONS.COMPLETE_SETUP,
    };
  }

  return null;
}
